import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthTokens } from '@/lib/cognito';

export type UserRole = 'users' | 'lawyers' | 'admins';

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
  role: UserRole;
}

interface AuthState {
  tokens: AuthTokens | null;
  user: AuthUser | null;
  hydrated: boolean;
  setSession: (tokens: AuthTokens, user: AuthUser) => void;
  clearSession: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      tokens: null,
      user: null,
      hydrated: false,
      setSession: (tokens, user) => set({ tokens, user }),
      clearSession: () => set({ tokens: null, user: null }),
      setHydrated: () => set({ hydrated: true }),
    }),
    {
      name: 'parecer-auth',
      partialize: (s) => ({ tokens: s.tokens, user: s.user }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Backfill: older sessions persisted user.userId='' because login didn't
          // decode the Cognito `sub` from the idToken. Recompute from the JWT now
          // so existing sessions get a proper userId without a re-login.
          if (state.tokens?.idToken && (!state.user || !state.user.userId)) {
            const recovered = userFromIdToken(state.tokens.idToken, state.user?.email);
            if (recovered) state.user = recovered;
          }
          state.setHydrated();
        }
      },
    },
  ),
);

function decodePayload(idToken: string): Record<string, unknown> | null {
  try {
    const parts = idToken.split('.');
    const part = parts[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function decodeTokenRole(idToken: string): UserRole {
  const payload = decodePayload(idToken);
  if (!payload) return 'users';
  const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
  if (groups.includes('admins')) return 'admins';
  if (groups.includes('lawyers')) return 'lawyers';
  return 'users';
}

/**
 * Build the AuthUser object from a Cognito idToken. The Cognito `sub` is the
 * stable identifier we use everywhere as `userId` (matches the `senderId` on
 * messages, the `ownerId` / `lawyerId` on tasks, etc).
 *
 * Pass `fallbackEmail` if you logged the user in by email and the token's
 * email claim might be absent for some reason.
 */
export function userFromIdToken(idToken: string, fallbackEmail?: string): AuthUser | null {
  const payload = decodePayload(idToken);
  if (!payload) return null;
  const sub = payload['sub'] as string | undefined;
  if (!sub) return null;
  const email = (payload['email'] as string | undefined) ?? fallbackEmail ?? '';
  const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
  const role: UserRole = groups.includes('admins')
    ? 'admins'
    : groups.includes('lawyers')
      ? 'lawyers'
      : 'users';
  const name = (payload['given_name'] as string | undefined) ?? (payload['name'] as string | undefined);
  return { userId: sub, email, role, ...(name ? { name } : {}) };
}
